import {Text, Icon, TextInput, Button} from '@/ui-kits';
import React, {useEffect, useState} from 'react';
import {useFormik} from 'formik';
import {DataDeleteValidation} from '@/utils/schema';
import {useDispatch, useSelector} from 'react-redux';
import {patientAccountCancelation, patientAccountDeletion} from '@/services/redux/slices';
import {accountDeletionStatusSelector, authDataSelector, isAccountDeletionLoadingSelector} from '@/services/redux/selectors';
import {PatientAccountTicketRequestStatus} from '@/constants';
import {DeleteConfirmModal} from './DeleteConfirmModal';
import {useConfimationModal} from '@/hooks';

interface initialvaluesType {
  email: string;
  val: string;
}

const initialValues: initialvaluesType = {
  email: '',
  val: '',
};

export const DeleteAccount = () => {
  // ===========================
  // hooks
  // ===========================
  const dispatch = useDispatch();
  const {showModal} = useConfimationModal();
  const {values, handleSubmit, errors, touched, handleChange, handleBlur, setFieldValue, isValid, dirty} = useFormik({
    initialValues,
    onSubmit,
    validationSchema: DataDeleteValidation,
  });
  
  // ===========================
  // selectors
  // ===========================
  const ticketStatus = useSelector(accountDeletionStatusSelector);
  const authdata = useSelector(authDataSelector);
  const isAccountDeletionLoading = useSelector(isAccountDeletionLoadingSelector);
  // ===========================
  // state
  // ===========================
  const [confirmModel, setConfirmModel] = useState(false);
  
  // ===========================
  // functions
  // ===========================
  function onSubmit(values: initialvaluesType) {
    dispatch(patientAccountDeletion({contact_email: values.email}));
    values.val = '';
  }
  
  function handleCancel() {
    showModal({
      onConfirmHandler: () => dispatch(patientAccountCancelation()),
      cancelButtonText: 'Close',
      confirmButtonText: 'Confirm Cancel',
      header: <span>Are you sure you want to cancel your<br />account deletion request?</span> as any
    });
    
    
  }
  
  function handleDeleteAccount() {
    setConfirmModel(true);
  }
  
  function confirmDelete() {
    setConfirmModel(false);
    handleSubmit();
  }
  // ===========================
  // side effects
  // ===========================
  useEffect(() => {
    if (authdata) setFieldValue('email', authdata.email_id);
  }, []);

useEffect(() => {
  if (ticketStatus === PatientAccountTicketRequestStatus.Cancelled) {
    setFieldValue('val', '');
  }
}, [ticketStatus]);

  return (
    <div className="border rounded-xl border-[#F05B5B66] md:p-6 p-4 mt-6 bg-[#FFF9F9]">
      <Text variant="title" className="font-FigtreeSemiBold text-ui_blue">
        Delete Account
      </Text>
      <Text variant="small" className="text-gray-500 mt-1 mb-2">
        Permanently delete your account and all associated data
      </Text>
      <div className="my-2 border-[#F05B5B66] border p-2 rounded-md flex gap-2 mt-5 bg-white">
        <Icon type="warning_setting" />
        <Text variant="small" className="text-red-400 ">
          <span className="font-bold text-red-500 leading-6">Warning :</span> This action is permanent after 30 days. All your health data, consultation
          history, uploaded files, and personal information will be permanently deleted. Data will be exported automatically in ZIP format and sent to your
          registered email before deletion.
        </Text>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <TextInput
          placeholder="Enter your email"
          disabled={true}
          label="Contact Email for data export"
          labelClassName="!font-FigtreeSemiBold !text-lg"
          required
          wrapperClassName="border bg-white"
          className="w-full md:w-[90%] mt-2"
          value={values.email}
          error={errors.email}
          touched={touched.email}
          onBlur={handleBlur('email')}
          isHoverable={true}
        />
        <TextInput
          disabled={![PatientAccountTicketRequestStatus.NotApplicable, PatientAccountTicketRequestStatus.Cancelled].includes(ticketStatus)}
          labelClassName="!font-FigtreeSemiBold !text-lg"
          placeholder="Type DELETE here"
          label="Type DELETE to confirm"
          value={values.val}
          onChangeText={handleChange('val')}
          className="w-full md:w-[90%] mt-2"
          wrapperClassName="border bg-white"
        />
      </div>

      <div className="flex gap-2 mt-7 ">
        {![PatientAccountTicketRequestStatus.NotApplicable, PatientAccountTicketRequestStatus.Deleted, PatientAccountTicketRequestStatus.Cancelled].includes(
          ticketStatus,
        ) && <Button isLoading={isAccountDeletionLoading} title="Cancel" variant="secondary" onClick={handleCancel} className="sm:w-[217px] w-full" />}
        {[PatientAccountTicketRequestStatus.NotApplicable, PatientAccountTicketRequestStatus.Cancelled].includes(ticketStatus) && (
          <Button isLoading={isAccountDeletionLoading} title="Delete My Account" disabled={!isValid || !dirty} onClick={handleDeleteAccount} className="sm:w-[217px] w-full" />
        )}
      </div>

      {ticketStatus === PatientAccountTicketRequestStatus.Paused && (
        <Text variant="small" className="flex items-start text-red-600 mt-4">
          <Icon type="cancel_icon" className="mr-2 mt-0.5 shrink-0" />
          Your account deletion cannot proceed right now as there are pending actions. Check your email for details.
        </Text>
      )}

      {ticketStatus === PatientAccountTicketRequestStatus.Resumed && (
        <Text variant="small" className="flex items-start text-green-600 mt-4">
          All pending actions are resolved. Your account deletion process is resumed now.
        </Text>
      )}

      {confirmModel && <DeleteConfirmModal onClose={() => setConfirmModel(false)} onConfirm={confirmDelete} open={confirmModel} />}
    </div>
  );
};
